import Dialog from 'components/general/dialogs/Dialog';
import LabeledInput from 'components/general/inputs/LabeledInput';
import { GENERIC_ERROR_MESSAGE } from 'config';
import { useFormikForm, useSnackbar, useUser } from 'hooks';
import { SatelliteApi } from 'middleware/SatelliteApi/api';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

interface IProps {
  dialogOpen: boolean;
  onSuccess: () => void;
  onClose: () => void;
}

interface IForm {
  currentPassword: string;
  sso: boolean;
}

const defaultValues = { currentPassword: '', sso: false };

const passwordSchema = Yup.object().shape({
  currentPassword: Yup.string().when('sso', {
    is: false,
    then: Yup.string().required('Enter your existing password.'),
  }),
});

const AuthDialog = (props: IProps) => {
  const { dialogOpen, onSuccess, onClose } = props;
  const user = useUser();
  const {
    User: {
      actions: { login, startExternalPatch },
    },
  } = SatelliteApi;
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useDispatch();

  const authenticate = (values: IForm) => {
    setLoading(true);
    if (values.sso) {
      // Redirect to SSO
      dispatch(
        startExternalPatch({
          queryParams: {
            identityProvider: user.externalIdentityProvider,
          },
          successCallback: (resp: { redirect: string }) => {
            window.location.href = resp.redirect;
          },
          failureCallback: () => {
            enqueueSnackbar(GENERIC_ERROR_MESSAGE, { variant: 'error' });
            setLoading(false);
          },
        })
      );
    } else {
      // Do a "login" just to see if it authenticates
      dispatch(
        login({
          email: user.email,
          password: values.currentPassword,
          successCallback: () => {
            setFieldValue('currentPassword', '');
            setLoading(false);
            onSuccess();
          },
          failureCallback: () => {
            // authentication failure callback
            setFieldValue('currentPassword', '');
            enqueueSnackbar('Incorrect password.');
            setLoading(false);
          },
        })
      );
    }
  };
  const { formik } = useFormikForm<IForm, IForm>(defaultValues, authenticate, passwordSchema, {
    ...defaultValues,
    sso: Boolean(user.externalIdentityProvider),
  });
  const { handleSubmit, setFieldValue, getFieldProps, values } = formik;

  return (
    <Dialog
      prompt="Confirm changes"
      open={dialogOpen}
      onSubmit={handleSubmit}
      onClose={() => {
        setFieldValue('currentPassword', '');
        onClose();
      }}
      loading={loading}
    >
      {values.sso ? (
        <p>
          You will be redirected to Single Sign-On (SSO) to verify your identity before making
          changes.
        </p>
      ) : (
        <>
          <p>Please enter your current password to confirm your changes.</p>
          <LabeledInput
            {...getFieldProps('currentPassword')}
            type="password"
            label="Password"
            placeholder="Current Password"
            autoFocus
          />
        </>
      )}
    </Dialog>
  );
};

export default AuthDialog;
